import { Navigate, useRoutes } from 'react-router-dom';
import { useAppSelector } from './store';

import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import Login from './pages/Login';
import DashboardApp from './pages/Dashboard';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import NotFound from './pages/Page404';

import Profile from './pages/Profile';
import UserProfile from './pages/User/Profile';
import VerifyWhatsappOtp from './pages/VerifyWhatsappLogin';
import Users from './pages/Users';
import Country from './pages/Country';
import Region from './pages/Region';
import RegionForm from './pages/Region/Form';
import Category from './pages/Category';
import AddOnCategory from './pages/AddOnCategory';
import BundleCategory from './pages/BundleCategory';
import BundleSubcategory from './pages/BundleSubcategory';
import BundleProduct from './pages/BundleProduct';
import BundleProductForm from './pages/BundleProduct/Form/BundleProductForm';
import AddOnProduct from './pages/AddOnProduct';
import AddOnProductForm from './pages/AddOnProduct/Form';
import AddOnProductVariant from './pages/AddOnProductVariant';
import AddOnProductVariantForm from './pages/AddOnProductVariant/Form';
import Color from './pages/Color';
import CakeBox from './pages/CakeBox';
import Shape from './pages/Shape';
import ShapeForm from './pages/Shape/Form';
import Flavor from './pages/Flavor';
import FlavorForm from './pages/Flavor/Form';
import ToppingsCategory from './pages/ToppingsCategory';
import ToppingsCategoryForm from './pages/ToppingsCategory/Form';
import ToppingsCategoryItem from './pages/ToppingsCategoryItem';
import ToppingsCategoryItemForm from './pages/ToppingsCategoryItem/Form';
import CategoryCake from './pages/CategoryCake';
import CategoryCakeForm from './pages/CategoryCake/Form';
import PromoCode from './pages/PromoCode';
import PromoCodeForm from './pages/PromoCode/Form';
import Order from './pages/Order';
import OrderView from './pages/Order/View';
import EditOrder from './pages/Order/Edit';
import CreateOrder from './pages/Order/Create/index';
import TempOrder from './pages/TempOrder';
import ViewUser from './pages/Users/View';
import EditUser from './pages/Users/Edit';
import Notification from './pages/Notification';
import Settings from './pages/Settings';
import DeliveryTimeSlots from './pages/DeliveryTimeSlots';
import DynamicWord from './pages/DynamicWord';
import RewardPointsSettings from './pages/RewardPointsSettings';
import FollowUpCodesSettings from './pages/FollowUpCodesSettings';
import ProductBadgeSettings from './pages/ProductBadgeSettings';
import Slider from './pages/Slider';
import ContactForm from './pages/ContactForm';
import FAQ from './pages/FAQ';
import FAQForm from './pages/FAQ/Form';
import Partners from './pages/Partners';
import PartnerView from './pages/Partners/View';
import PartnerAudit from './pages/Partners/Audit';
import PartnerPaymentResult from './pages/PartnerPaymentResult';
import { isPartnerApiEnabled } from './utils/partnerFeature';

type Props = {
  ROLES: Array<string>;
  children: any;
  id?: string;
  ids?: Array<string>;
  type?: Array<string>;
  checkAccess?: boolean;
};

const Router = () => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const { data: WhatsappLoginOtpData } = useAppSelector(
    (state) => state.whatsappLogin
  );

  const ProtectedRouteWrapper = ({ ROLES, children }: Props) => {
    if (ROLES.includes(userRole) === true) {
      return <>{children}</>;
    } else {
      return <></>;
    }
  };

  return useRoutes([
    {
      path: '/',
      element: accessToken ? <DashboardLayout /> : <Navigate to='/login' />,
      children: [
        {
          path: '/',
          element: <DashboardApp />,
        },
        {
          path: 'analytics-dashboard',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'finance']}>
              <AnalyticsDashboard />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'users',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Users />
            </ProtectedRouteWrapper>
          ),
        },
        ...(isPartnerApiEnabled()
          ? [
              {
                path: 'partners',
                element: (
                  <ProtectedRouteWrapper ROLES={['admin']}>
                    <Partners />
                  </ProtectedRouteWrapper>
                ),
              },
              {
                path: 'partners/view/:id',
                element: (
                  <ProtectedRouteWrapper ROLES={['admin']}>
                    <PartnerView />
                  </ProtectedRouteWrapper>
                ),
              },
              {
                path: 'partners/audit',
                element: (
                  <ProtectedRouteWrapper ROLES={['admin']}>
                    <PartnerAudit />
                  </ProtectedRouteWrapper>
                ),
              },
            ]
          : [
              {
                path: 'partners/*',
                element: <Navigate to='/' replace />,
              },
            ]),
        {
          path: 'country',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Country />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'slider',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <Slider />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'notification',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Notification />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'settings',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Settings />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'delivery-time-slots',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <DeliveryTimeSlots />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'dynamic-word',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <DynamicWord />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'reward-points',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <RewardPointsSettings />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'follow-up-codes',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <FollowUpCodesSettings />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'product-badges',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <ProductBadgeSettings />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'faq',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <FAQ />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'faq/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <FAQForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'faq/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <FAQForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'contact-form',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ContactForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'region',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <Region />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'region/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <RegionForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'region/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <RegionForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'occasion',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <Category />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'color',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <Color />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'cake-box',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <CakeBox />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'shape',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <Shape />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'shape/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ShapeForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'shape/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ShapeForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'flavor',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <Flavor />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'flavor/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <FlavorForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'flavor/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <FlavorForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'order',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'finance', 'orderManager']}>
              <Order />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'order/view/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'finance', 'orderManager']}>
              <OrderView />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'order/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'finance', 'orderManager']}>
              <EditOrder />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'order/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'orderManager']}>
              <CreateOrder />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'temp-order',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'orderManager']}>
              <TempOrder />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'temp-order/view/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'orderManager']}>
              <OrderView />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'temp-order/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'orderManager']}>
              <EditOrder />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'promo-code',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PromoCode />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'promo-code/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PromoCodeForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'promo-code/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <PromoCodeForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-category',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnCategory />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'bundle-category',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <BundleCategory />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'bundle-subcategory',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <BundleSubcategory />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'bundle-product',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <BundleProduct />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'bundle-product/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <BundleProductForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'bundle-product/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <BundleProductForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-product',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnProduct />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-product/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnProductForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-product/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnProductForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-product-variant',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnProductVariant />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-product-variant/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnProductVariantForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'addon-product-variant/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <AddOnProductVariantForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'toppings-category',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ToppingsCategory />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'toppings-category/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ToppingsCategoryForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'toppings-category/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ToppingsCategoryForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'toppings-category-item',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ToppingsCategoryItem />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'toppings-category-item/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ToppingsCategoryItemForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'toppings-category-item/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <ToppingsCategoryItemForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'occasion-cake',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <CategoryCake />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'occasion-cake/create',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <CategoryCakeForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'occasion-cake/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin', 'contentManager']}>
              <CategoryCakeForm />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'users/view/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <ViewUser />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'users/edit/:id',
          element: (
            <ProtectedRouteWrapper ROLES={['admin']}>
              <EditUser />
            </ProtectedRouteWrapper>
          ),
        },
        {
          path: 'user/profile',
          element: (
            <ProtectedRouteWrapper
              ROLES={['admin', 'contentManager', 'finance', 'orderManager']}
            >
              <UserProfile />
            </ProtectedRouteWrapper>
          ),
        },
        { path: 'profile', element: <Profile /> },
      ],
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: '/', element: <Navigate to='/' /> },

        {
          path: 'login',
          element: accessToken ? <Navigate to='/' /> : <Login />,
        },
        {
          path: 'verify-whatsapp-otp',
          element:
            accessToken && !WhatsappLoginOtpData?.requestId ? (
              <Navigate to='/login' />
            ) : (
              <VerifyWhatsappOtp />
            ),
        },
        {
          path: 'partner/payment/result',
          element: <PartnerPaymentResult />,
        },
      ],
    },

    // {
    //   path: '/',
    //   children: [
    //     {
    //       path: 'organizations',
    //       element:
    //         accessToken && userRole !== 'superAdmin' ? (
    //           <OrganizationList />
    //         ) : (
    //           <Navigate to='/login' />
    //         ),
    //     },
    //     { path: '404', element: <NotFound /> },
    //     { path: '*', element: <Navigate to='/404' /> },
    //   ],
    // },

    { path: '404', element: <NotFound /> },
    {
      path: '*',
      element: (
        <Navigate
          to='/404'
          replace
        />
      ),
    },
  ]);
};
export default Router;
